import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {
    BymoPagePropsContentSettingsMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common/types/doppe-hideable-value';
import type {CurrencyCode, RichTextContent} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, asArray, immerProduce} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeActionCTAType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    DoppeExternalMembershipPlanProvider,
    doppeExternalMembershipPlanProviderResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-external-membership-plans';
import type {DoppeDtoMembershipPlan} from '../../client-server-common/types/doppe-dto-membership-plan';

export namespace DoppeActionTypeHoppPricingPlan {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        pricingPlanPageLink: string;
        pricingPlanTitle: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        pricingPlanImages: WixMediaResource[] | null;
        priceData: DoppeHideableValue<DoppeActionPrice>;
        pricingPlanBenefits: string[];
        couponCode: DoppeHideableValue<string>;
        ribbon: DoppeHideableValue<string>;
        pricingPlanProvider: DoppeExternalMembershipPlanProvider | null;
        pricingPlanProviderId: string | null;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppPricingPlanActionContentSettingsMetadata = {
    pricingPlanPageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    pricingPlanTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    pricingPlanImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    pricingPlanBenefits: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    couponCode: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppPricingPlan.ActionSettings>

export const doppeActionTypeHoppPricingPlan = doppeActionTypeDefineMetadata<DoppeActionTypeHoppPricingPlan.ActionSettings>({
    id: 'io.bymo.action/hopp-pricing-plan',
    resolveActionSettings: action => {
        const { ...restSettings } = action.settings;
        return {
            pricingPlanPageLink: '',
            pricingPlanTitle: doppeHideableValueCreateHidden(''),
            description: doppeHideableValueCreateHidden(''),
            pricingPlanImages: null,
            priceData: doppeHideableValueCreateHidden({ 
                actualPrice: 0,
                discountPrice: undefined,
                currencyCode: 'USD'
            }),
            pricingPlanBenefits: [],
            couponCode: doppeHideableValueCreateHidden(''),
            ribbon: doppeHideableValueCreateHidden(''),
            pricingPlanProvider: DoppeExternalMembershipPlanProvider.Hopp,
            pricingPlanProviderId: null,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppPricingPlanActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.OpenPricingPlan]: true,
            }
        },
        supportsContentSections: { customContentSectionsTypes: {} }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,

    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.pricingPlanImages, []) ?? []));
    },
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.pricingPlanImages, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    resolveCouponData: actionSettings => {
        return {
            showCouponCode: doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.couponCode, ''),
            couponCode: doppeHideableValueGetVisibleValue(actionSettings.couponCode, ''),
            couponDescription: '',
            couponTitle: doppeHideableValueGetVisibleValue(actionSettings.pricingPlanTitle, ''),
        }
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.pricingPlanProvider !== null && actionSettings.pricingPlanProviderId !== null ?
                doppeExternalMembershipPlanProviderResolveBusinessEntityReference(
                    actionSettings.pricingPlanProvider,
                    actionSettings.pricingPlanProviderId
                ) :
                null
        ])
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.pricingPlanImages) {
                actionSettings.pricingPlanImages = actionSettings.pricingPlanImages.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});

export function doppeActionTypeHoppPricingPlanResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeHoppPricingPlan.BaseActionSettings,
    pricingPlan: DoppeDtoMembershipPlan
): DoppeActionTypeHoppPricingPlan.BaseActionSettings {

    return {
        ...actionSettings,
        ribbon: doppeHideableValueCreateHidden(''),
        pricingPlanImages: pricingPlan.media ? [pricingPlan.media] : null,
        pricingPlanPageLink: pricingPlan.membershipPageUrl ?? actionSettings?.pricingPlanPageLink ?? ``,
        priceData: pricingPlan.pricing ? {
            actualPrice: parseFloat(pricingPlan.pricing.price?.value ?? '0') || 0,
            currencyCode: (pricingPlan.pricing.price?.currency ?? 'USD') as CurrencyCode
        } : actionSettings?.priceData ?? doppeHideableValueCreateHidden({
            actualPrice: 0,
            discountPrice: undefined,
            currencyCode: 'USD'
        }),
        pricingPlanBenefits: pricingPlan.perks ?? actionSettings?.pricingPlanBenefits ?? [],
        pricingPlanTitle: pricingPlan?.name ?? doppeHideableValueCreateHidden(''),
        description: pricingPlan?.description ?? doppeHideableValueCreateHidden(''),
    }
}

export function convertDoppeDtoMembershipPlanToHoppPricingPlanSettings(options: {
    pricingPlan: DoppeDtoMembershipPlan,
    pricingPlanSettings?: DoppeActionTypeHoppPricingPlan.ActionSettings
}) {
    const { pricingPlan, pricingPlanSettings = {} } = options;
    return {
        ...doppeActionContentSectionSettingsDefaultValuesMigration(pricingPlanSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ ctaButtons: [] }, false),
        ...pricingPlanSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(pricingPlanSettings),
        pricingPlanPageLink: pricingPlan.membershipPageUrl ?? '',
        pricingPlanTitle: doppeHideableValueCreateHidden(pricingPlan.name ?? ''),
        description: doppeHideableValueCreateHidden(pricingPlan.description ?? ''),
        pricingPlanImages: pricingPlan.media ? [pricingPlan.media] : null,
        priceData: pricingPlan.pricing ? doppeHideableValueCreateHidden({
            actualPrice: parseFloat(pricingPlan.pricing.price?.value ?? '0') || 0,
            currencyCode: (pricingPlan.pricing.price?.currency ?? 'USD') as CurrencyCode
        }) : doppeHideableValueCreateHidden({
            actualPrice: 0,
            discountPrice: undefined,
            currencyCode: 'USD' as CurrencyCode
        }),
        pricingPlanBenefits: pricingPlan.perks ?? [],
        couponCode: doppeHideableValueCreateHidden(''),
        ribbon: doppeHideableValueCreateHidden(''),
        pricingPlanProvider: DoppeExternalMembershipPlanProvider.Hopp,
        pricingPlanProviderId: pricingPlan.id,
    } satisfies DoppeActionTypeHoppPricingPlan.ActionSettings
} 